import { Dialog, Loader } from "@/components";
import { Badge, Button, Input, Label } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Copy,
  GripVertical, Plus, Save, Search, Send, Trash2, X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { DESCRIPTION_MAX_LENGTH, GL_SEARCH_LIMIT, TITLE_MAX_LENGTH } from "../consts";
import { StatusBadge } from "../StatusBadge";
import { GlPickerProps, MoveButtonsProps } from "../types";
import useFinancialVersionBuilder from "./useFinancialVersionBuilder";

const MoveButtons = ({ index, length, onMove, label }: MoveButtonsProps) => (
  <div className="flex flex-col">
    <button type="button" className="text-gray-400 hover:text-primary disabled:opacity-30" disabled={index === 0}
      onClick={() => onMove(index - 1)} aria-label={`Move ${label} up`}><ArrowUp className="w-3.5 h-3.5" /></button>
    <button type="button" className="text-gray-400 hover:text-primary disabled:opacity-30" disabled={index === length - 1}
      onClick={() => onMove(index + 1)} aria-label={`Move ${label} down`}><ArrowDown className="w-3.5 h-3.5" /></button>
  </div>
);

const GlPicker = ({ glOptions, usage, ownKey, onAdd }: GlPickerProps) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    return glOptions
      .filter((g) => !s || `${g.glAccountNumber} ${g.accountShortDesc}`.toLowerCase().includes(s))
      .slice(0, GL_SEARCH_LIMIT);
  }, [glOptions, q]);
  return (
    <div className="relative" ref={ref}>
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        placeholder="Search GL number or name to add" className="h-9 bg-white" startIcon={<Search className="w-4 h-4" />} />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-md border bg-white shadow-lg">
          {results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">No GL accounts match</p>
          ) : results.map((g) => {
            const used = usage.get(g.glMasterId);
            const here = used?.subKey === ownKey;
            return (
              <button key={g.glMasterId} type="button" disabled={!!used}
                onClick={() => { onAdd(g.glMasterId); setQ(""); }}
                className={cn("flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm",
                  used ? "cursor-not-allowed bg-gray-50 text-gray-400" : "hover:bg-primary/5")}>
                <span><span className="font-medium">{g.glAccountNumber}</span> – {g.accountShortDesc}</span>
                {used && <span className="shrink-0 text-xs">{here ? "Added" : `In ${used.where}`}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Financial Statement Version builder (admin). Category > Subcategory > GL accounts; the order at every level
 * (including GLs inside a subcategory) is the order reports show. Save Draft sends the whole structure in one
 * call; Publish makes the version read-only (changes then go into a New Version).
 */
const FinancialVersionBuilder = () => {
  const {
    community,
    structure,
    isLoading,
    template,
    readOnly,
    draft,
    update,
    setCat,
    setSub,
    addCategory,
    addSub,
    collapsed,
    toggle,
    allCollapsed,
    toggleAllCollapsed,
    errors,
    showUnassigned,
    setShowUnassigned,
    confirm,
    closeConfirm,
    runConfirm,
    bind,
    isOver,
    dirty,
    busy,
    isCopying,
    glOptions,
    glById,
    usage,
    unassigned,
    subCount,
    onSaveDraft,
    onPublish,
    onCancel,
    onCopyVersion,
    moveCat,
    moveSub,
    moveGl,
    addGl,
    removeGl,
    removeSub,
    onDeleteCategory,
  } = useFinancialVersionBuilder();

  if (isLoading || !structure) return <div className="p-6"><Loader /></div>;

  return (
    <div className="p-4 md:p-6 pb-28 flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button type="button" variant="outline" size="icon" onClick={onCancel} aria-label="Back"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h2 className="text-lg md:text-xl font-semibold">
              {readOnly ? "Financial Version" : "Build Financial Version"}
              <span className="font-normal text-gray-500"> — {community?.communityId == template?.communityId ? community?.name : `Community ${template?.communityId}`}</span>
            </h2>
            <p className="text-xs text-gray-500">Financial Statement Versions / {template?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          {readOnly
            ? <Button onClick={onCopyVersion} disabled={isCopying}><Copy className="w-4 h-4" /> New Version</Button>
            : <Button onClick={onSaveDraft} disabled={busy || !dirty}><Save className="w-4 h-4" /> Save Draft</Button>}
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-4 md:p-5 flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-start">
          <div className="flex flex-col gap-1">
            <Label htmlFor="fv-title">Financial Version Name *</Label>
            <Input id="fv-title" value={draft.title} maxLength={TITLE_MAX_LENGTH} disabled={readOnly}
              onChange={(e) => update((d) => ({ ...d, title: e.target.value }))} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="fv-desc">Description</Label>
            <Input id="fv-desc" value={draft.description} maxLength={DESCRIPTION_MAX_LENGTH} disabled={readOnly}
              onChange={(e) => update((d) => ({ ...d, description: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2 md:pt-6">
            <Badge variant="outline" className="rounded-full px-3">v{template?.versionNo}</Badge>
            <StatusBadge status={template?.status} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 border-t pt-3">
          <span>{draft.cats.length} categor{draft.cats.length === 1 ? "y" : "ies"}</span>
          <span>{subCount} subcategor{subCount === 1 ? "y" : "ies"}</span>
          <span><span className="font-medium text-gray-800">{usage.size}</span> of {glOptions.length} active GL accounts assigned</span>
          {unassigned.length > 0 && (
            <button type="button" className="text-primary text-xs underline" onClick={() => setShowUnassigned((v) => !v)}>
              {showUnassigned ? "Hide" : "Show"} {unassigned.length} unassigned
            </button>
          )}
        </div>
        {showUnassigned && unassigned.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {unassigned.map((g) => (
              <span key={g.glMasterId} className="rounded-full border bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600">
                {g.glAccountNumber} – {g.accountShortDesc}
              </span>
            ))}
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <p className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4" /> Please fix the following</p>
          <ul className="mt-1 list-disc pl-6">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      {readOnly && (
        <p className="rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
          Published versions are read-only. Use <b>New Version</b> to make changes; the copy keeps this order.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">Drag <GripVertical className="inline w-3 h-3" /> or use the arrows to change the order. Reports follow this order.</p>
        <div className="flex gap-2">
          <Button variant="outline" disabled={draft.cats.length === 0} onClick={toggleAllCollapsed}>
            {allCollapsed ? <><ChevronsUpDown className="w-4 h-4" /> Expand All</> : <><ChevronsDownUp className="w-4 h-4" /> Collapse All</>}
          </Button>
          {!readOnly && <Button onClick={addCategory}><Plus className="w-4 h-4" /> Add Category</Button>}
        </div>
      </div>

      {draft.cats.length === 0 && (
        <div className="rounded-xl border border-dashed bg-white p-8 text-center text-sm text-gray-500">
          No categories yet. {!readOnly && <>Start with <b>Add Category</b> (e.g. Income, Operating Expenses).</>}
        </div>
      )}

      {draft.cats.map((cat, ci) => {
        const isCollapsed = collapsed.has(cat.key);
        const glCount = cat.subs.reduce((n, s) => n + s.gls.length, 0);
        return (
          <div key={cat.key} {...bind("cat", ci, moveCat, !readOnly)}
            className={cn("rounded-xl border bg-white shadow-sm", isOver("cat", ci) && "ring-2 ring-primary/50")}>
            <div className="flex flex-wrap items-center gap-2 px-3 py-3 md:px-4">
              {!readOnly && <GripVertical className="w-4 h-4 cursor-grab text-gray-400" />}
              {!readOnly && <MoveButtons index={ci} length={draft.cats.length} label="category" onMove={(to) => moveCat(ci, to)} />}
              <button type="button" onClick={() => toggle(cat.key)} aria-label={isCollapsed ? "Expand" : "Collapse"}>
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <span className="text-xs font-semibold text-primary">CATEGORY {String(ci + 1).padStart(2, "0")}</span>
              <Input value={cat.name} disabled={readOnly} placeholder="Category name" maxLength={TITLE_MAX_LENGTH}
                className="h-9 w-full sm:w-72 bg-white" onChange={(e) => setCat(cat.key, (c) => ({ ...c, name: e.target.value }))} />
              <div className="ml-auto flex items-center gap-2">
                <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700 border-blue-200">{cat.subs.length} subcategor{cat.subs.length === 1 ? "y" : "ies"}</Badge>
                <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700 border-blue-200">{glCount} GL{glCount === 1 ? "" : "s"}</Badge>
                {!readOnly && (
                  <button type="button" className="text-red-500 hover:text-red-700" aria-label="Delete category"
                    onClick={() => onDeleteCategory(cat, ci)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {!isCollapsed && (
              <div className="border-t px-3 pb-3 md:px-4">
                <div className="hidden md:grid grid-cols-[auto_minmax(180px,1fr)_2fr_auto] gap-3 px-2 py-2 text-xs font-medium text-gray-500">
                  <span className="w-10" /><span>Subcategory Name</span><span>Assigned GL Accounts (in report order)</span><span className="w-6" />
                </div>
                {cat.subs.map((sub, si) => {
                  const subScope = `sub:${cat.key}`;
                  return (
                    <div key={sub.key} {...bind(subScope, si, (from, to) => moveSub(cat.key, from, to), !readOnly)}
                      className={cn("grid grid-cols-1 md:grid-cols-[auto_minmax(180px,1fr)_2fr_auto] gap-3 rounded-lg border-b px-2 py-3 last:border-b-0",
                        isOver(subScope, si) && "bg-primary/5")}>
                      <div className="flex items-start gap-1 pt-2">
                        {!readOnly && <GripVertical className="w-4 h-4 cursor-grab text-gray-400" />}
                        {!readOnly && <MoveButtons index={si} length={cat.subs.length} label="subcategory" onMove={(to) => moveSub(cat.key, si, to)} />}
                      </div>
                      <Input value={sub.name} disabled={readOnly} placeholder="Subcategory name" maxLength={TITLE_MAX_LENGTH} className="h-9 bg-white"
                        onChange={(e) => setSub(cat.key, sub.key, (s) => ({ ...s, name: e.target.value }))} />
                      <div className="flex flex-col gap-2">
                        {sub.gls.length > 0 && (
                          <ol className="flex flex-col gap-1">
                            {sub.gls.map((id, gi) => {
                              const g = glById.get(id);
                              const glScope = `gl:${sub.key}`;
                              return (
                                <li key={id} {...bind(glScope, gi, (from, to) => moveGl(cat.key, sub.key, from, to), !readOnly)}
                                  className={cn("flex items-center gap-2 rounded-md border bg-gray-50 px-2 py-1 text-sm", isOver(glScope, gi) && "ring-2 ring-primary/40")}>
                                  {!readOnly && <GripVertical className="w-3.5 h-3.5 cursor-grab text-gray-400" />}
                                  <span className="w-6 text-right text-xs text-gray-400">{gi + 1}.</span>
                                  <span className="flex-1"><span className="font-medium">{g?.glAccountNumber ?? id}</span> – {g?.accountShortDesc ?? ""}
                                    {g && g.active === false && <span className="ml-2 text-xs text-amber-600">(inactive GL)</span>}</span>
                                  {!readOnly && <MoveButtons index={gi} length={sub.gls.length} label="GL" onMove={(to) => moveGl(cat.key, sub.key, gi, to)} />}
                                  {!readOnly && (
                                    <button type="button" className="text-gray-400 hover:text-red-600" aria-label="Remove GL"
                                      onClick={() => removeGl(cat.key, sub.key, id)}>
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                        {!readOnly && (
                          <GlPicker glOptions={glOptions} usage={usage} ownKey={sub.key}
                            onAdd={(id) => addGl(cat.key, sub.key, id)} />
                        )}
                        {readOnly && sub.gls.length === 0 && <span className="text-sm text-gray-400">No GL accounts</span>}
                      </div>
                      <div className="pt-2">
                        {!readOnly && (
                          <button type="button" className="text-red-500 hover:text-red-700" aria-label="Delete subcategory"
                            onClick={() => removeSub(cat.key, sub.key)}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!readOnly && (
                  <Button type="button" variant="outline" className="mt-3 border-dashed" onClick={() => addSub(cat.key)}>
                    <Plus className="w-4 h-4" /> Add Subcategory
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-[1600px] items-center justify-end gap-2 px-4 py-3 md:px-6">
            <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button variant="outline" onClick={onSaveDraft} disabled={busy || !dirty}><Save className="w-4 h-4" /> Save Draft</Button>
            <Button onClick={onPublish} disabled={busy}><Send className="w-4 h-4" /> Publish Version</Button>
          </div>
        </div>
      )}

      <Dialog open={!!confirm} setOpen={(o: boolean) => !o && closeConfirm()} hideFooter title={confirm?.title}>
        <div className="flex flex-col gap-4">
          <p className="text-sm">{confirm?.text}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeConfirm}>Cancel</Button>
            <Button variant={confirm?.danger ? "destructive" : "default"} onClick={runConfirm}>
              {confirm?.danger ? "Yes, continue" : "Publish"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinancialVersionBuilder;
